/**
 * Single-asset lineage transfer — legacy TP转出 / FC转出 / RP转出.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Decimal from 'decimal.js';
import { ApiError } from '../api/client';
import { usersAPI } from '../api/users';
import { useAuthStore } from '../store/auth';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedBottomActionBar,
  ThemedField,
  ThemedInput,
  ThemedNotice,
  ThemedPanel,
  ThemedTextarea,
} from '../components/ui/ThemedSurface';
import { BUYFUEL_COLORS } from '@/constants';

export type LineageTransferAsset = 'tp' | 'fc' | 'rp';

const ASSET_META: Record<
  LineageTransferAsset,
  { titleKey: string; titleDefault: string; balanceKey: string; balanceDefault: string; amountPlaceholder: string }
> = {
  tp: {
    titleKey: 'lineageTransfer.tpTitle',
    titleDefault: 'TP转出',
    balanceKey: 'lineageTransfer.tpBalance',
    balanceDefault: 'TP数量',
    amountPlaceholder: '请输入10的倍数',
  },
  fc: {
    titleKey: 'lineageTransfer.fcTitle',
    titleDefault: 'FC转出',
    balanceKey: 'lineageTransfer.fcBalance',
    balanceDefault: 'FC数量',
    amountPlaceholder: '请输入转出的数量',
  },
  rp: {
    titleKey: 'lineageTransfer.rpTitle',
    titleDefault: 'RP转出',
    balanceKey: 'lineageTransfer.rpBalance',
    balanceDefault: 'RP数量',
    amountPlaceholder: '请输入转出的数量',
  },
};

function fmtNum(v: string, digits = 3): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits });
}

export function LineageAssetTransferPage({ asset }: { asset: LineageTransferAsset }) {
  const meta = ASSET_META[asset];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const profile = useAuthStore((s) => s.profile);

  const [toUsername, setToUsername] = useState('');
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [showSecurity, setShowSecurity] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pickDownlineOpen, setPickDownlineOpen] = useState(false);
  const [pickUplineOpen, setPickUplineOpen] = useState(false);
  const [downlineQ, setDownlineQ] = useState('');

  const { data: bal } = useQuery({
    queryKey: ['user', 'balances'],
    queryFn: () => usersAPI.getBalances(),
  });
  const balance = bal?.balances?.[asset] ?? '0';

  const { data: contacts } = useQuery({
    queryKey: ['lineage-transfer', 'contacts', downlineQ.trim()],
    queryFn: () => usersAPI.lineageContacts(downlineQ.trim() || undefined),
    enabled: asset === 'tp' || asset === 'rp' || asset === 'fc',
  });
  const uplines = contacts?.uplines?.length
    ? contacts.uplines
    : contacts?.upline
      ? [contacts.upline]
      : [];

  const trimmedUser = toUsername.trim();
  const previewQuery = useQuery({
    queryKey: ['lineage-transfer', 'single', 'preview', asset, trimmedUser],
    queryFn: () => usersAPI.lineageSingleTransferPreview({ to_username: trimmedUser, asset }),
    enabled: trimmedUser.length >= 2,
  });

  const nickname =
    previewQuery.data?.recipient?.nickname?.trim() ||
    (previewQuery.isSuccess
      ? previewQuery.data?.recipient?.username?.trim() || trimmedUser
      : '');
  const minAmount = previewQuery.data?.minimum ?? (asset === 'fc' ? '0.05' : asset === 'tp' ? '10' : '1');
  const stepAmount = previewQuery.data?.step ?? minAmount;
  const feePct = previewQuery.data?.fee_pct ?? contacts?.tp_fee_pct ?? '0';

  const securityQuestion =
    ((profile as Record<string, unknown> | null)?.security_question as string | undefined) ??
    t('common.defaultSecurityQuestion');

  const submitMut = useMutation({
    mutationFn: () =>
      usersAPI.lineageSingleTransfer({
        asset,
        amount: amount.trim(),
        to_username: trimmedUser,
        transaction_password: txPassword,
        security_answer: securityAnswer,
        memo: memo.trim() || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user', 'balances'] });
      setError(null);
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('lineageTransfer.failed', '转出失败'));
    },
  });

  const amountHint = useMemo(() => {
    if (asset === 'tp') {
      return t('lineageTransfer.tpMinStep', '单次转出不能低于 {{min}} TP，按 {{step}} 的整倍数转出', {
        min: minAmount,
        step: stepAmount,
      });
    }
    if (asset === 'fc') {
      return t('lineageTransfer.fcMinStep', '单次转出不能低于 {{min}} FC，按 {{step}} 的整倍数', {
        min: minAmount,
        step: stepAmount,
      });
    }
    return t('lineageTransfer.rpMinStep', '单次转出最低 {{min}} RP', { min: minAmount });
  }, [asset, minAmount, stepAmount, t]);

  const feePreview = useMemo(() => {
    if (asset !== 'tp' || Number(feePct) <= 0) return null;
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return null;
    const fee = (n * Number(feePct)) / 100;
    return {
      fee: fee.toFixed(2),
      net: (n - fee).toFixed(2),
    };
  }, [asset, amount, feePct]);

  useEffect(() => {
    const dir = searchParams.get('direction');
    if (!contacts) return;
    if (dir === 'up' && (contacts.uplines?.[0]?.username || contacts.upline?.username)) {
      setToUsername(contacts.uplines?.[0]?.username || contacts.upline?.username || '');
    }
  }, [contacts, searchParams]);

  function validate(): string | null {
    if (!trimmedUser) return t('lineageTransfer.recipientRequired', '请输入接收账户');
    let amt: Decimal;
    try {
      amt = new Decimal(amount.trim());
    } catch {
      return t('lineageTransfer.amountRequired', '请输入转出数量');
    }
    if (!amt.isFinite() || amt.lte(0)) return t('lineageTransfer.amountRequired', '请输入转出数量');
    const min = new Decimal(minAmount);
    const step = new Decimal(stepAmount);
    if (min.isFinite() && amt.lt(min)) return amountHint;
    // Use Decimal.mod — JS `n % 0.05` falsely rejects 0.25/1/10 etc. (float precision)
    if (step.isFinite() && step.gt(0) && !amt.mod(step).eq(0)) {
      return t('lineageTransfer.stepInvalid', '请按 {{step}} 的整倍数填写', { step: stepAmount });
    }
    if (amt.gt(new Decimal(balance))) {
      return t('lineageTransfer.insufficient', '{{asset}} 余额不足', { asset: asset.toUpperCase() });
    }
    if (!txPassword.trim()) return t('common.enterTxPassword');
    if (!securityAnswer.trim()) return t('common.enterSecurityAnswer');
    if (previewQuery.isSuccess && previewQuery.data && !previewQuery.data.qualifies) {
      return t('lineageTransfer.notQualified', '不符合血缘转出条件（上转需双方 A3+）');
    }
    return null;
  }

  function handleSubmit() {
    setError(null);
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    submitMut.mutate();
  }

  function selectUpline(username: string) {
    setToUsername(username);
    setPickDownlineOpen(false);
    setPickUplineOpen(false);
    setError(null);
  }

  function selectDownline(username: string) {
    setToUsername(username);
    setPickDownlineOpen(false);
    setPickUplineOpen(false);
    setDownlineQ('');
    setError(null);
  }

  useEffect(() => {
    if (!submitMut.isSuccess) return;
    const timer = window.setTimeout(() => navigate(-1), 1200);
    return () => window.clearTimeout(timer);
  }, [submitMut.isSuccess, navigate]);

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader
          title={t(meta.titleKey, meta.titleDefault)}
          onBack={() => navigate(-1)}
          theme={C}
        />

        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px] pb-28">
          <ThemedPanel className="p-4" theme={C}>
            <div className="text-sm" style={{ color: C.muted }}>
              {t(meta.balanceKey, meta.balanceDefault)}
            </div>
            <div className="mt-1 font-mono text-3xl font-bold" style={{ color: C.gold }}>
              {fmtNum(balance, asset === 'fc' ? 3 : 2)}
            </div>
          </ThemedPanel>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => {
                setPickUplineOpen(false);
                setPickDownlineOpen((v) => !v);
                setError(null);
              }}
              className="rounded-xl border px-3 py-3 text-left text-xs active:opacity-80"
              style={{ borderColor: C.border, color: C.text, background: C.glassBg }}
            >
              <div className="font-semibold">{t('lineageTransfer.toDownline', '转给旗下')}</div>
              <div className="mt-1" style={{ color: C.muted }}>
                {t('lineageTransfer.toDownlineHint', '推荐链下级')}
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                if (!uplines.length) {
                  setError(t('lineageTransfer.noUpline', '暂无上级'));
                  return;
                }
                if (uplines.length === 1) {
                  selectUpline(uplines[0]!.username);
                  return;
                }
                setPickDownlineOpen(false);
                setPickUplineOpen((v) => !v);
                setError(null);
              }}
              className="rounded-xl border px-3 py-3 text-left text-xs active:opacity-80"
              style={{ borderColor: C.border, color: C.text, background: C.glassBg }}
            >
              <div className="font-semibold">{t('lineageTransfer.toUpline', '转给旗上')}</div>
              <div className="mt-1" style={{ color: C.muted }}>
                {uplines[0]?.username ?? t('lineageTransfer.noUpline', '暂无上级')}
              </div>
            </button>
          </div>

          {pickDownlineOpen ? (
            <ThemedPanel className="space-y-2 p-3" theme={C}>
              <div className="text-xs font-semibold" style={{ color: C.muted }}>
                {t('lineageTransfer.pickDownline', '选择下级（推荐链）')}
              </div>
              <ThemedInput
                value={downlineQ}
                onChange={(e) => setDownlineQ(e.target.value)}
                placeholder={t('lineageTransfer.searchRelated', '搜索用户名 / 编号')}
              />
              {(contacts?.downlines ?? []).map((d) => (
                <button
                  key={d.username}
                  type="button"
                  onClick={() => selectDownline(d.username)}
                  className="flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm active:opacity-80"
                  style={{ borderColor: C.border, color: C.text }}
                >
                  <span>{d.username}</span>
                  <span className="text-xs" style={{ color: C.muted }}>
                    {d.nickname?.trim() || d.level_code}
                  </span>
                </button>
              ))}
              {!contacts?.downlines.length ? (
                <div className="text-xs" style={{ color: C.muted }}>
                  {downlineQ.trim()
                    ? t('lineageTransfer.noRelatedMatch', '没有匹配的推荐链账户')
                    : t('lineageTransfer.searchRelatedHint', '输入用户名或编号查找隔代下级')}
                </div>
              ) : null}
            </ThemedPanel>
          ) : null}

          {pickUplineOpen && uplines.length ? (
            <ThemedPanel className="space-y-2 p-3" theme={C}>
              <div className="text-xs font-semibold" style={{ color: C.muted }}>
                {t('lineageTransfer.pickUpline', '选择上级（推荐链）')}
              </div>
              {uplines.map((u) => (
                <button
                  key={u.username}
                  type="button"
                  onClick={() => selectUpline(u.username)}
                  className="flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm active:opacity-80"
                  style={{ borderColor: C.border, color: C.text }}
                >
                  <span>{u.username}</span>
                  <span className="text-xs" style={{ color: C.muted }}>
                    {u.nickname?.trim() || u.level_code}
                  </span>
                </button>
              ))}
            </ThemedPanel>
          ) : null}

          <ThemedNotice tone="warning">
            <div className="mb-1 text-xs font-semibold">#提示#</div>
            <ol className="list-decimal space-y-1 pl-4 text-xs leading-relaxed">
              {asset === 'rp' ? (
                <li>{t('lineageTransfer.ruleRpBoth', '可向推荐链上下级转出 RP（可隔无限代），无等级限制')}</li>
              ) : asset === 'tp' ? (
                <>
                  <li>{t('lineageTransfer.ruleTpFree', '可向推荐链上下级转出；A5 及以上免手续费')}</li>
                  <li>
                    {Number(feePct) > 0
                      ? t('lineageTransfer.ruleTpFee', 'A3～A4 转出收取 {{pct}}% 手续费', { pct: feePct })
                      : t('lineageTransfer.ruleTpNoFee', '当前级别转出 TP 免手续费')}
                  </li>
                </>
              ) : (
                <>
                  <li>
                    {t(
                      'lineageTransfer.ruleUp',
                      '级别为 A3 及以上的空间站会员可以向级别为 A3 及以上的推荐链上级（可隔无限代）上转',
                    )}
                  </li>
                  <li>
                    {t('lineageTransfer.ruleDown', '可以向推荐链下级玩家下转（可隔无限代）')}
                  </li>
                </>
              )}
              <li>{amountHint}</li>
            </ol>
          </ThemedNotice>

          <ThemedPanel className="space-y-4 p-4" theme={C}>
            <ThemedField label={t('lineageTransfer.amount', '转出数量')} required>
              <ThemedInput
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={meta.amountPlaceholder}
              />
            </ThemedField>

            {feePreview ? (
              <div className="rounded-lg border px-3 py-2 text-xs" style={{ borderColor: C.border, color: C.muted }}>
                {t('lineageTransfer.feeSummary', '手续费 {{fee}} TP，对方实收 {{net}} TP', {
                  fee: feePreview.fee,
                  net: feePreview.net,
                })}
              </div>
            ) : null}

            <ThemedField label={t('lineageTransfer.recipient', '接收账户')} required>
              <ThemedInput
                value={toUsername}
                onChange={(e) => setToUsername(e.target.value)}
                placeholder={t('lineageTransfer.recipientPlaceholder', '请输入接收玩家用户名')}
                autoCapitalize="none"
              />
            </ThemedField>

            <ThemedField label={t('lineageTransfer.nickname', '玩家昵称')}>
              <ThemedInput
                readOnly
                value={
                  previewQuery.isFetching && !nickname
                    ? t('lineageTransfer.nicknameLoading', '查询中…')
                    : nickname
                }
                placeholder={t('lineageTransfer.nicknameHint', '输入用户名会自动显示')}
              />
            </ThemedField>

            <TransactionSecurity
              txPassword={txPassword}
              onTxPasswordChange={setTxPassword}
              showSecurity={showSecurity}
              onShowSecurityChange={setShowSecurity}
              securityAnswer={securityAnswer}
              onSecurityAnswerChange={setSecurityAnswer}
              securityQuestionText={securityQuestion}
              disabled={submitMut.isPending}
            />

            <ThemedField label={t('lineageTransfer.memo', '备注信息')}>
              <ThemedTextarea
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder={t('lineageTransfer.memoPlaceholder', '备注信息,可不填写(500字以内)')}
                rows={3}
                maxLength={500}
              />
            </ThemedField>
          </ThemedPanel>

          {previewQuery.isError ? (
            <ThemedNotice tone="error">
              {previewQuery.error instanceof ApiError
                ? previewQuery.error.message
                : t('lineageTransfer.previewFailed', '无法验证接收账户')}
            </ThemedNotice>
          ) : null}

          {submitMut.isSuccess ? (
            <ThemedNotice tone="success">{t('lineageTransfer.success', '转出成功')}</ThemedNotice>
          ) : null}

          {error ? <ThemedNotice tone="error">{error}</ThemedNotice> : null}
        </div>

        <ThemedBottomActionBar background={BUYFUEL_COLORS.bottomBarBg} className="pb-5 pt-3">
          <button
            type="button"
            disabled={submitMut.isPending || submitMut.isSuccess}
            onClick={handleSubmit}
            className="h-11 w-full rounded-xl border text-sm font-semibold disabled:opacity-50"
            style={{
              background: C.buttonBg,
              borderColor: C.buttonBorder,
              color: C.text,
            }}
          >
            {submitMut.isPending
              ? t('lineageTransfer.submitting', '提交中…')
              : t('lineageTransfer.confirm', '确定转出')}
          </button>
        </ThemedBottomActionBar>
      </div>
    </div>
  );
}
